import React, { useState, useEffect, useContext, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../config/firebaseConfig";
import { UserContext } from "../../../App";
import { Navigate } from "react-router-dom";
import "./IncidentManagementPage.css";

const IncidentManagementPage = ({ setPageTitle }) => {
  const { role } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [filters, setFilters] = useState({
    status: "all",
    riskLevel: "all",
    startDate: "",
    endDate: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: 20,
  });

  const fetchIncidents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const getIncidentsFunction = httpsCallable(functions, "getIncidents");
      const result = await getIncidentsFunction({
        page: pagination.currentPage,
        limit: pagination.limit,
        ...filters,
        // Convert empty strings to null for the backend
        startDate: filters.startDate || null,
        endDate: filters.endDate || null,
      });

      setIncidents(result.data.incidents);
      setPagination((prev) => ({
        ...prev,
        ...result.data.pagination,
      }));
    } catch (error) {
      console.error("Error fetching incidents:", error);
      setError("Failed to load incidents. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.limit]);

  useEffect(() => {
    setPageTitle("Incident Management - Law 25 Compliance");
    fetchIncidents();
  }, [setPageTitle, fetchIncidents]);

  // Strict admin-only check
  if (role !== "admin") {
    return <Navigate to="/home" replace />;
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateIncident = async (incidentData) => {
    try {
      setIsSubmitting(true);
      setActionError(null);
      const createIncidentFunction = httpsCallable(functions, "createIncident");
      await createIncidentFunction(incidentData);
      setShowCreateModal(false);
      fetchIncidents();
    } catch (error) {
      console.error("Error creating incident:", error);
      setActionError(
        error.message || "Failed to create incident. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteIncident = (incident) => {
    setSelectedIncident(incident);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      setActionError(null);
      const deleteIncidentFunction = httpsCallable(functions, "deleteIncident");
      await deleteIncidentFunction({ incidentId: selectedIncident.id });
      setShowDeleteModal(false);
      setSelectedIncident(null);
      fetchIncidents();
    } catch (error) {
      console.error("Error deleting incident:", error);
      setActionError(
        error.message || "Failed to delete incident. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case "low":
        return "#28a745";
      case "medium":
        return "#ffc107";
      case "high":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ongoing":
        return "#ffc107";
      case "resolved":
        return "#28a745";
      default:
        return "#6c757d";
    }
  };

  if (loading) {
    return (
      <div className="incident-management-page">
        <div className="loading">Loading incidents...</div>
      </div>
    );
  }

  return (
    <div className="incident-management-page">
      {actionError && (
        <div className="action-error-message">
          {actionError}
          <button onClick={() => setActionError(null)}>×</button>
        </div>
      )}
      <div className="page-header">
        <h1>Confidentiality Incident Management</h1>
        <p className="compliance-note">
          Law 25 Compliance - All incidents are retained for 5 years minimum
        </p>
        <button
          className="create-incident-btn"
          onClick={() => setShowCreateModal(true)}
        >
          + Report New Incident
        </button>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label htmlFor="status">Status:</label>
          <select
            id="status"
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="riskLevel">Risk Level:</label>
          <select
            id="riskLevel"
            value={filters.riskLevel}
            onChange={(e) => handleFilterChange("riskLevel", e.target.value)}
          >
            <option value="all">All</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        <div className="filter-group">
          <label htmlFor="startDate">Start Date:</label>
          <input
            type="date"
            id="startDate"
            value={filters.startDate}
            onChange={(e) => handleFilterChange("startDate", e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label htmlFor="endDate">End Date:</label>
          <input
            type="date"
            id="endDate"
            value={filters.endDate}
            onChange={(e) => handleFilterChange("endDate", e.target.value)}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="incidents-table">
        <table>
          <thead>
            <tr>
              <th>Incident ID</th>
              <th>Discovery Date</th>
              <th>Nature</th>
              <th>Risk Level</th>
              <th>Status</th>
              <th>Affected Individuals</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((incident) => (
              <tr key={incident.id}>
                <td className="incident-id">
                  {incident.id.substring(0, 8)}...
                </td>
                <td>{formatDate(incident.discoveryDateTime)}</td>
                <td className="incident-nature">{incident.nature}</td>
                <td>
                  <span
                    className="risk-badge"
                    style={{
                      backgroundColor: getRiskLevelColor(
                        incident.riskAssessment.overallRiskLevel
                      ),
                    }}
                  >
                    {incident.riskAssessment.overallRiskLevel}
                  </span>
                </td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: getStatusColor(
                        incident.resolution.status
                      ),
                    }}
                  >
                    {incident.resolution.status}
                  </span>
                </td>
                <td>{incident.scope.numberOfAffectedIndividuals}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => setSelectedIncident(incident)}
                  >
                    View Details
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteIncident(incident)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <button
          disabled={pagination.currentPage === 1}
          onClick={() =>
            setPagination({
              ...pagination,
              currentPage: pagination.currentPage - 1,
            })
          }
        >
          Previous
        </button>
        <span>
          Page {pagination.currentPage} of {pagination.totalPages}(
          {pagination.totalIncidents} total incidents)
        </span>
        <button
          disabled={pagination.currentPage === pagination.totalPages}
          onClick={() =>
            setPagination({
              ...pagination,
              currentPage: pagination.currentPage + 1,
            })
          }
        >
          Next
        </button>
      </div>

      {showCreateModal && (
        <CreateIncidentModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateIncident}
        />
      )}

      {selectedIncident && (
        <IncidentDetailModal
          incident={selectedIncident}
          onClose={() => setSelectedIncident(null)}
          onUpdate={fetchIncidents}
        />
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm Deletion</h2>
              <button
                className="close-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete this incident? This action
                cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={confirmDelete}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CreateIncidentModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    nature: "",
    discoveryDateTime: "",
    scope: {
      numberOfAffectedIndividuals: 0,
      description: "",
    },
    affectedSystems: [],
    typeOfInformationCompromised: [],
    initialAssessment: {
      sensitivityLevel: "medium",
      potentialConsequences: [],
      likelihoodOfMisuse: "medium",
      overallRiskLevel: "medium",
      assessmentNotes: "",
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const informationTypes = [
    "Personal identifiers (names, addresses)",
    "Contact information (email, phone)",
    "Academic records",
    "Financial information",
    "Health information",
    "Authentication credentials",
    "Biometric data",
    "Other sensitive information",
  ];

  const systemTypes = [
    "Learning Management System",
    "Student Information System",
    "Email system",
    "File storage",
    "Database",
    "Authentication system",
    "Third-party service",
    "Other",
  ];

  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2>Report New Confidentiality Incident</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>

            <div className="form-group">
              <label>Nature of Incident *</label>
              <textarea
                value={formData.nature}
                onChange={(e) =>
                  setFormData({ ...formData, nature: e.target.value })
                }
                placeholder="Describe the nature of the incident..."
                required
              />
            </div>

            <div className="form-group">
              <label>Discovery Date & Time *</label>
              <input
                type="datetime-local"
                value={formData.discoveryDateTime}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discoveryDateTime: e.target.value,
                  })
                }
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Scope & Impact</h3>

            <div className="form-group">
              <label>Number of Affected Individuals</label>
              <input
                type="number"
                value={formData.scope.numberOfAffectedIndividuals}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scope: {
                      ...formData.scope,
                      numberOfAffectedIndividuals:
                        parseInt(e.target.value) || 0,
                    },
                  })
                }
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Scope Description</label>
              <textarea
                value={formData.scope.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    scope: {
                      ...formData.scope,
                      description: e.target.value,
                    },
                  })
                }
                placeholder="Describe the scope of the incident..."
              />
            </div>

            <div className="form-group">
              <label>Type of Information Compromised</label>
              <div className="checkbox-group">
                {informationTypes.map((type) => (
                  <label key={type} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.typeOfInformationCompromised.includes(
                        type
                      )}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...formData.typeOfInformationCompromised, type]
                          : formData.typeOfInformationCompromised.filter(
                              (t) => t !== type
                            );
                        setFormData({
                          ...formData,
                          typeOfInformationCompromised: updated,
                        });
                      }}
                    />
                    {type}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Affected Systems</label>
              <div className="checkbox-group">
                {systemTypes.map((system) => (
                  <label key={system} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.affectedSystems.includes(system)}
                      onChange={(e) => {
                        const updated = e.target.checked
                          ? [...formData.affectedSystems, system]
                          : formData.affectedSystems.filter(
                              (s) => s !== system
                            );
                        setFormData({ ...formData, affectedSystems: updated });
                      }}
                    />
                    {system}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Initial Risk Assessment</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Sensitivity Level</label>
                <select
                  value={formData.initialAssessment.sensitivityLevel}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      initialAssessment: {
                        ...formData.initialAssessment,
                        sensitivityLevel: e.target.value,
                      },
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="form-group">
                <label>Likelihood of Misuse</label>
                <select
                  value={formData.initialAssessment.likelihoodOfMisuse}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      initialAssessment: {
                        ...formData.initialAssessment,
                        likelihoodOfMisuse: e.target.value,
                      },
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>

              <div className="form-group">
                <label>Overall Risk Level</label>
                <select
                  value={formData.initialAssessment.overallRiskLevel}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      initialAssessment: {
                        ...formData.initialAssessment,
                        overallRiskLevel: e.target.value,
                      },
                    })
                  }
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Assessment Notes</label>
              <textarea
                value={formData.initialAssessment.assessmentNotes}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    initialAssessment: {
                      ...formData.initialAssessment,
                      assessmentNotes: e.target.value,
                    },
                  })
                }
                placeholder="Additional notes on risk assessment..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary">
              Create Incident Report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const IncidentDetailModal = ({ incident, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);

  const updateIncident = async (updates) => {
    try {
      setLoading(true);
      const updateIncidentFunction = httpsCallable(functions, "updateIncident");
      await updateIncidentFunction({
        incidentId: incident.id,
        updates,
      });
      onUpdate();
      onClose();
    } catch (error) {
      console.error("Error updating incident:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content extra-large">
        <div className="modal-header">
          <h2>Incident Details - {incident.id.substring(0, 8)}</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="incident-tabs">
          <button
            className={activeTab === "overview" ? "active" : ""}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            className={activeTab === "assessment" ? "active" : ""}
            onClick={() => setActiveTab("assessment")}
          >
            Risk Assessment
          </button>
          <button
            className={activeTab === "containment" ? "active" : ""}
            onClick={() => setActiveTab("containment")}
          >
            Containment
          </button>
          <button
            className={activeTab === "notifications" ? "active" : ""}
            onClick={() => setActiveTab("notifications")}
          >
            Notifications
          </button>
          <button
            className={activeTab === "resolution" ? "active" : ""}
            onClick={() => setActiveTab("resolution")}
          >
            Resolution
          </button>
        </div>

        <div className="tab-content">
          {activeTab === "overview" && (
            <IncidentOverviewTab incident={incident} />
          )}
          {activeTab === "assessment" && (
            <RiskAssessmentTab incident={incident} onUpdate={updateIncident} />
          )}
          {activeTab === "containment" && (
            <ContainmentTab incident={incident} onUpdate={updateIncident} />
          )}
          {activeTab === "notifications" && (
            <NotificationsTab incident={incident} onUpdate={updateIncident} />
          )}
          {activeTab === "resolution" && (
            <ResolutionTab incident={incident} onUpdate={updateIncident} />
          )}
        </div>
      </div>
    </div>
  );
};

// Tab Components
const IncidentOverviewTab = ({ incident }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-CA");
  };

  return (
    <div className="overview-tab">
      <div className="info-section">
        <h3>Basic Information</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Incident ID:</label>
            <span>{incident.id}</span>
          </div>
          <div className="info-item">
            <label>Discovery Date:</label>
            <span>{formatDate(incident.discoveryDateTime)}</span>
          </div>
          <div className="info-item">
            <label>Report Date:</label>
            <span>{formatDate(incident.reportDateTime)}</span>
          </div>
          <div className="info-item">
            <label>Current Status:</label>
            <span className="status-badge">{incident.resolution.status}</span>
          </div>
        </div>
      </div>

      <div className="info-section">
        <h3>Nature of Incident</h3>
        <p>{incident.nature}</p>
      </div>

      <div className="info-section">
        <h3>Scope & Impact</h3>
        <div className="info-grid">
          <div className="info-item">
            <label>Affected Individuals:</label>
            <span>{incident.scope.numberOfAffectedIndividuals}</span>
          </div>
          <div className="info-item">
            <label>Risk Level:</label>
            <span className="risk-badge">
              {incident.riskAssessment.overallRiskLevel}
            </span>
          </div>
        </div>
        <p>{incident.scope.description}</p>

        {incident.scope.typeOfInformationCompromised.length > 0 && (
          <div>
            <h4>Information Types Compromised:</h4>
            <ul>
              {incident.scope.typeOfInformationCompromised.map(
                (type, index) => (
                  <li key={index}>{type}</li>
                )
              )}
            </ul>
          </div>
        )}

        {incident.scope.systemsAffected.length > 0 && (
          <div>
            <h4>Systems Affected:</h4>
            <ul>
              {incident.scope.systemsAffected.map((system, index) => (
                <li key={index}>{system}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const RiskAssessmentTab = ({ incident, onUpdate }) => {
  return (
    <div className="assessment-tab">
      <div className="info-section">
        <h3>Current Risk Assessment</h3>
        <div className="assessment-grid">
          <div className="assessment-item">
            <label>Sensitivity Level:</label>
            <span className="level-badge">
              {incident.riskAssessment.sensitivityLevel}
            </span>
          </div>
          <div className="assessment-item">
            <label>Likelihood of Misuse:</label>
            <span className="level-badge">
              {incident.riskAssessment.likelihoodOfMisuse}
            </span>
          </div>
          <div className="assessment-item">
            <label>Overall Risk Level:</label>
            <span className="risk-badge">
              {incident.riskAssessment.overallRiskLevel}
            </span>
          </div>
        </div>

        {incident.riskAssessment.assessmentNotes && (
          <div>
            <h4>Assessment Notes:</h4>
            <p>{incident.riskAssessment.assessmentNotes}</p>
          </div>
        )}

        {incident.riskAssessment.potentialConsequences.length > 0 && (
          <div>
            <h4>Potential Consequences:</h4>
            <ul>
              {incident.riskAssessment.potentialConsequences.map(
                (consequence, index) => (
                  <li key={index}>{consequence}</li>
                )
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const ContainmentTab = ({ incident, onUpdate }) => {
  return (
    <div className="containment-tab">
      <div className="info-section">
        <h3>Containment Measures</h3>
        {incident.containmentMeasures.actionsTaken.length > 0 ? (
          <ul>
            {incident.containmentMeasures.actionsTaken.map((action, index) => (
              <li key={index}>{action}</li>
            ))}
          </ul>
        ) : (
          <p>No containment measures recorded yet.</p>
        )}

        {incident.containmentMeasures.effectivenessAssessment && (
          <div>
            <h4>Effectiveness Assessment:</h4>
            <p>{incident.containmentMeasures.effectivenessAssessment}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const NotificationsTab = ({ incident, onUpdate }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "Not sent";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-CA");
  };

  return (
    <div className="notifications-tab">
      <div className="info-section">
        <h3>Notification Status</h3>

        <div className="notification-item">
          <h4>Individual Notifications</h4>
          <div className="notification-details">
            <p>
              <strong>Required:</strong>{" "}
              {incident.notifications.individualNotification.required
                ? "Yes"
                : "No"}
            </p>
            <p>
              <strong>Date Sent:</strong>{" "}
              {formatDate(
                incident.notifications.individualNotification.dateSent
              )}
            </p>
            <p>
              <strong>Method:</strong>{" "}
              {incident.notifications.individualNotification.method ||
                "Not specified"}
            </p>
            <p>
              <strong>Recipients:</strong>{" "}
              {incident.notifications.individualNotification.recipientCount}
            </p>
          </div>
        </div>

        <div className="notification-item">
          <h4>CAI (Commission d'accès à l'information) Notification</h4>
          <div className="notification-details">
            <p>
              <strong>Required:</strong>{" "}
              {incident.notifications.caiNotification.required ? "Yes" : "No"}
            </p>
            <p>
              <strong>Date Sent:</strong>{" "}
              {formatDate(incident.notifications.caiNotification.dateSent)}
            </p>
            <p>
              <strong>Reference Number:</strong>{" "}
              {incident.notifications.caiNotification.referenceNumber ||
                "Not assigned"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResolutionTab = ({ incident, onUpdate }) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return "Not resolved";
    const date = timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString("en-CA");
  };

  return (
    <div className="resolution-tab">
      <div className="info-section">
        <h3>Resolution Status</h3>
        <p>
          <strong>Status:</strong>{" "}
          <span className="status-badge">{incident.resolution.status}</span>
        </p>
        <p>
          <strong>Resolution Date:</strong>{" "}
          {formatDate(incident.resolution.dateResolved)}
        </p>

        {incident.resolution.resolutionSteps.length > 0 && (
          <div>
            <h4>Resolution Steps:</h4>
            <ul>
              {incident.resolution.resolutionSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </div>
        )}

        {incident.resolution.preventiveMeasures.length > 0 && (
          <div>
            <h4>Preventive Measures:</h4>
            <ul>
              {incident.resolution.preventiveMeasures.map((measure, index) => (
                <li key={index}>{measure}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncidentManagementPage;
